import React, { useEffect, useState } from "react";
import {
  graphcms,
  QUERY_MEMBERS_AC_YR_LIST,
  QUERY_MEMBERS_BY_AC_YR,
  QUERY_MEMBERS_STILL_POSITION,
} from "../Graphql/Queries";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";

function MemberCard({ member }) {
  const [imageLoading, setImageLoading] = useState(true);
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    setImgSrc(
      member.image
        ? member.image.url
        : "https://placehold.co/400x600?text=No%5CnImage",
    );
  }, [member]);

  return (
    <div className="w-56 h-auto pb-6 mb-4 bg-slate-50 mx-auto rounded-xl drop-shadow-xl text-center overflow-hidden">
      <div className="relative w-56 h-72">
        {imageLoading && (
          <Skeleton className="absolute inset-0 w-full h-full" />
        )}
        <img
          className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoading ? "opacity-0" : "opacity-100"}`}
          src={imgSrc}
          alt={`${member.title}. ${member.name}`}
          onLoad={() => setImageLoading(false)}
          onError={() => {
            setImgSrc("https://placehold.co/400x600?text=No%5CnImage");
            // Ensure loading state is cleared even on error so placeholder shows
            setImageLoading(false);
          }}
        />
      </div>
      <h3 className="text-center font-semibold mt-2">{`${member.title}. ${member.name}`}</h3>
      <p className="text-center text-sm pb-1">{member.position}</p>
    </div>
  );
}

function Committee() {
  const [members, setMembers] = useState([]);
  const [academicYR, setAcademicYR] = useState([]);
  const [loading, setLoading] = useState(true);

  let latestAcYr = academicYR[academicYR.length - 1];
  let membersNames = "";

  const [searchParams] = useSearchParams();
  const acYear = searchParams.get("acYr");
  let metaAcYr = acYear || latestAcYr;

  useEffect(() => {
    // fetch academic years
    graphcms.request(QUERY_MEMBERS_AC_YR_LIST).then((res) => {
      const uniqueYears = Array.from(
        new Set(res.members.map((member) => member.academicYear)),
      );
      uniqueYears.sort();
      setAcademicYR(uniqueYears);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    if (acYear) {
      graphcms
        .request(QUERY_MEMBERS_BY_AC_YR, { academicYear: acYear })
        .then((res) => {
          console.log("Members Data:", res.members);
          setMembers(res.members);
          setLoading(false);
        })
        .catch((err) => console.error(err));
    } else {
      graphcms
        .request(QUERY_MEMBERS_STILL_POSITION)
        .then((res) => {
          console.log("Members Data:", res.members);
          setMembers(res.members);
          setLoading(false);
        })
        .catch((err) => console.error(err));
    }
  }, [acYear]);

  function renderMembers(academicYear) {
    setLoading(true);
    graphcms.request(QUERY_MEMBERS_BY_AC_YR, { academicYear }).then((res) => {
      setMembers(res.members);
      setLoading(false);
    });
    metaAcYr = academicYear;
  }

  members?.forEach((member) => {
    membersNames += member.name + ", ";
  });

  return (
    <>
      <Helmet>
        <title>Committee Members - IEEE CIS SBC UOJ</title>
        <meta
          name="description"
          content={`Members of the IEEE CIS SBC UOJ in ${metaAcYr} are ${membersNames}`}
        />
        <link rel="canonical" href="/cis/committee" />
      </Helmet>
      <div className="mt-10 mb-20 container flex flex-col gap-20 lg:w-10/12 mx-auto">
        <section className="members">
          <div className="w-full flex justify-between items-center gap-y-5">
            <h1 className="text-3xl font-extrabold uppercase">
              committee members {acYear ? `(${acYear})` : ""}
            </h1>
            {!acYear && (
              <select
                name="acYr"
                id="acYr"
                className="border-2 border-black/40 rounded-lg text-base py-3 px-5 focus:border-none tracking-widest"
                onChange={(e) => renderMembers(e.target.value)}
                defaultValue={latestAcYr}>
                {academicYR.map((year) => (
                  <option
                    key={year}
                    value={year}
                    selected={latestAcYr === year ? true : false}>
                    {year}
                  </option>
                ))}
              </select>
            )}
          </div>
        </section>
        <section className="members">
          {loading ? (
            <div className="grid gap-10 grid-cols-fluid">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="w-56 h-92 pb-6 mb-4 bg-slate-50 mx-auto rounded-xl drop-shadow-xl overflow-hidden">
                  <Skeleton className="w-56 h-72 rounded-t-xl" />
                  <div className="p-4 space-y-2 flex flex-col items-center">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid gap-10 grid-cols-fluid">
              {members?.map((member) => (
                <MemberCard key={member.id} member={member} />
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}

export default Committee;
